import { ref, toRef, toRefs, computed, defineComponent, watch, reactive } from "vue"
import { useAuth, role, currentWorkerId } from "./useAuth"
import { setError, setWarning, startLoading, stopLoading, clearNotifications } from "../components/Notification"

import { hasRole, hasRoleStartsWith } from "../utils/roles"
import { assignOrderAsync } from "../api"
import { usePaging } from "../components/usePaging"
import { EMPTY_GUID, DATE_FORMAT_DISPLAY } from "../utils/constants"

export const useWorkOrders = (defaultWorkOrderType, defaultFilters, { syncQueryParams = false } = {}) => {
  const { fetchAuthAsync } = useAuth()
  const orders = ref([])
  const router = VueRouter.useRouter()
  const route = VueRouter.useRoute()
  const sortBy = ref("")
  const location = ref({ lat: null, lon: null })

  const selectOrderAsync = async (order, openInNewWindow = false) => {
    if (order.isInjected && hasRoleStartsWith(role.value, ["Worker"])) {
      console.log("Assigning order because selected.", currentWorkerId.value, order.id)
      await assignOrderAsync(fetchAuthAsync, currentWorkerId.value, order.id)
    }

    const routeData = hasRole(role.value, ["Customer"])
      ? router.resolve({ name: "ServiceOrderCustomer", params: { workOrderId: order.id } })
      : router.resolve({ name: "ServiceOrder", params: { workOrderId: order.id } })
    if (openInNewWindow) {
      window.open(routeData.href, "_blank")
    } else {
      router.push(routeData)
    }
  }

  const DEFAULT_FILTERS = () => {
    let overrideDefaults = null
    if (typeof defaultFilters === "function") overrideDefaults = defaultFilters()
    return {
      status: overrideDefaults?.status ?? "Active,Accepted,InProgress,Triage,Completed",
      type: defaultWorkOrderType,
      facility: overrideDefaults?.facility ?? "",
      device: "",
      nearFacilityId: "",
      customer: overrideDefaults?.customer ?? "",
      userCreated: "",
      searchTerm: "",
      orderNumber: "",
      materialID: "", // only production
      projectID: "", // only production
      ownerID: overrideDefaults?.ownerID ?? "",
      dueDate: "", // only production
      priority: null, // only production
      serialNumber: null,
      createdFrom: "",
      createdTo: "",
      completedFrom: "",
      completedTo: "",
      datePlannedFrom: "",
      datePlannedTo: "",

      finalizedFrom: "",
      finalizedTo: "",
      completedOrFinalizedYesterday: false,
      completedOrFinalizedToday: false,
      isWarranty: "undefined",
      isSpecial: "undefined",
      isAttention: "undefined",
      isAccepted: "undefined",
      hadUncompletedCommission: "undefined",
      isLeakageTest: "undefined",
      isService: "undefined",
      isCondenserCleaning: "undefined",
      isCondenserCleaningDry: "undefined",
      isCo2Check: "undefined",
      isSystemCheck: "undefined",
      needsTwo: "undefined",
      isThermometerCal: "undefined",
      isGeneratedPeriodic: "undefined",
      hasDatePlannedVisit: "undefined",
      internalOrderTypes: "", // only internal
      isDistanceAssignment: "undefined", // only service
    }
  }

  const applyQueryParams = () => {
    const q = route.query
    if (q.status) filters.status = q.status
    if (q.statuses) filters.status = q.statuses
    if (q.customer) filters.customer = q.customer
    if (q.customerID) filters.customer = q.customerID
    if (q.facility) filters.facility = q.facility
    if (q.facilityID) filters.facility = q.facilityID
    if (q.ownerID) filters.ownerID = q.ownerID
    if (q.userCreated) filters.userCreated = q.userCreated
    if (q.searchTerm) filters.searchTerm = q.searchTerm
    if (q.orderNumber) filters.orderNumber = q.orderNumber
    if (q.serialNumber) filters.serialNumber = q.serialNumber
    if (q.projectID) filters.projectID = q.projectID
    if (q.createdFrom) filters.createdFrom = q.createdFrom
    if (q.createdTo) filters.createdTo = q.createdTo
    if (q.completedFrom) filters.completedFrom = q.completedFrom
    if (q.completedTo) filters.completedTo = q.completedTo
    if (q.finalizedFrom) filters.finalizedFrom = q.finalizedFrom
    if (q.finalizedTo) filters.finalizedTo = q.finalizedTo
    if (q.datePlannedFrom) filters.datePlannedFrom = q.datePlannedFrom
    if (q.datePlannedTo) filters.datePlannedTo = q.datePlannedTo
    if (q.isWarranty) filters.isWarranty = q.isWarranty
    if (q.isSpecial) filters.isSpecial = q.isSpecial
    if (q.isAttention) filters.isAttention = q.isAttention
    if (q.isAccepted) filters.isAccepted = q.isAccepted
    if (q.hadUncompletedCommission) filters.hadUncompletedCommission = q.hadUncompletedCommission
    if (q.isLeakageTest) filters.isLeakageTest = q.isLeakageTest
    if (q.isService) filters.isService = q.isService
    if (q.isCondenserCleaning) filters.isCondenserCleaning = q.isCondenserCleaning
    if (q.isCondenserCleaningDry) filters.isCondenserCleaningDry = q.isCondenserCleaningDry
    if (q.isCo2Check) filters.isCo2Check = q.isCo2Check
    if (q.isSystemCheck) filters.isSystemCheck = q.isSystemCheck
    if (q.needsTwo) filters.needsTwo = q.needsTwo
    if (q.isThermometerCal) filters.isThermometerCal = q.isThermometerCal
    if (q.isGeneratedPeriodic) filters.isGeneratedPeriodic = q.isGeneratedPeriodic
    if (q.hasDatePlannedVisit) filters.hasDatePlannedVisit = q.hasDatePlannedVisit
    if (q.isDistanceAssignment) filters.isDistanceAssignment = q.isDistanceAssignment
    if (q.completedOrFinalizedYesterday)
      filters.completedOrFinalizedYesterday = q.completedOrFinalizedYesterday === "true"
    if (q.completedOrFinalizedToday) filters.completedOrFinalizedToday = q.completedOrFinalizedToday === "true"
  }
  const resetFilters = () => {
    Object.assign(filters, DEFAULT_FILTERS())
  }
  const filters = reactive(DEFAULT_FILTERS())

  // Restore from URL on first load
  if (syncQueryParams) applyQueryParams()

  const downloadAttachmentsAsync = async (attachmentType) => {
    clearNotifications()
    startLoading("getAllOrdersAsync")
    try {
      const raw1 = await fetchAuthAsync(generateWorkOrdersUrl(true), "GET")
      if (!Array.isArray(raw1.result)) {
        throw raw1
      }
      const guids = raw1.result.map((o) => o.id)
      console.log(guids)
      const batchSize = 133
      let notFoundCount = 0
      let batches = 0
      for (let i = 0; i < guids.length; i += batchSize) {
        batches++
        const batch = guids.slice(i, i + batchSize)
        try {
          const raw2 = await fetchAuthAsync(
            `${AZURE_URI}/Attachments/${filters.type}?attachmentType=${attachmentType}`,
            "POST",
            batch,
          )
          if (raw2.message === "No attachments found.") {
            notFoundCount++
          } else {
            console.log(raw2)
          }
        } catch (e) {
          console.warn(e)
        }
      }
      if (notFoundCount === batches) {
        setWarning({ message: "No attachments found." })
      }
    } catch (e) {
      setError(e)
    }
    stopLoading("getAllOrdersAsync")
  }

  const generateWorkOrdersUrl = (all = false) => {
    var url = `${AZURE_URI}/WorkOrders/${filters.type}/${filters.status}?1=1`
    if (!all) {
      url += `&pageNum=${paging.value.pageNum}&itemsPerPage=${paging.value.itemsPerPage}`
    } else {
      url += `&pageNum=1&itemsPerPage=65536`
    }
    if (filters.customer && filters.customer !== EMPTY_GUID) {
      url += `&customerId=${filters.customer}`
    }
    if (filters.facility && filters.facility !== EMPTY_GUID) {
      url += `&facilityId=${filters.facility}`
    }
    if (filters.device && filters.device !== EMPTY_GUID) {
      url += `&deviceId=${filters.device}`
    }
    if (filters.nearFacilityId && filters.nearFacilityId !== EMPTY_GUID) {
      url += `&nearFacilityId=${filters.nearFacilityId}`
    }
    if (filters.userCreated) {
      url += `&userCreatedId=${filters.userCreated}`
    }
    if (filters.searchTerm.trim()) {
      url += `&searchTerm=${filters.searchTerm}`
    }
    if (filters.createdFrom) {
      url += `&createdFrom=${filters.createdFrom}`
    }
    if (filters.createdTo) {
      url += `&createdTo=${filters.createdTo}`
    }
    if (filters.completedFrom) {
      url += `&completedFrom=${filters.completedFrom}`
    }
    if (filters.completedTo) {
      url += `&completedTo=${filters.completedTo}`
    }
    if (filters.finalizedFrom) {
      url += `&finalizedFrom=${filters.finalizedFrom}`
    }
    if (filters.finalizedTo) {
      url += `&finalizedTo=${filters.finalizedTo}`
    }
    if (filters.datePlannedFrom) {
      url += `&datePlannedFrom=${filters.datePlannedFrom}`
    }
    if (filters.datePlannedTo) {
      url += `&datePlannedTo=${filters.datePlannedTo}`
    }
    if (filters.completedOrFinalizedYesterday) {
      url += `&completedOrFinalizedYesterday=true`
    }
    if (filters.completedOrFinalizedToday) {
      url += `&completedOrFinalizedToday=true`
    }
    // new
    if (filters.materialID) {
      url += `&materialID=${filters.materialID}`
    }
    if (filters.projectID) {
      url += `&projectID=${filters.projectID}`
    }
    if (filters.orderNumber.trim()) {
      url += `&orderNumber=${filters.orderNumber}`
    }
    if (filters.ownerID) {
      url += `&ownerID=${filters.ownerID}`
    }
    if (filters.dueDate) {
      url += `&dueDate=${filters.dueDate}`
    }
    if (filters.priority) {
      url += `&priority=${filters.priority}`
    }
    if (filters.serialNumber) {
      url += `&serialNumber=${filters.serialNumber}`
    }
    if (filters.isWarranty && filters.isWarranty !== "undefined") {
      url += `&isWarranty=${filters.isWarranty}`
    }
    if (filters.isSpecial !== "undefined" && typeof filters.isSpecial !== "undefined") {
      url += `&isSpecial=${filters.isSpecial}`
    }
    if (filters.isAttention !== "undefined" && typeof filters.isAttention !== "undefined") {
      url += `&isAttention=${filters.isAttention}`
    }
    if (filters.isAccepted !== "undefined" && typeof filters.isAccepted !== "undefined") {
      url += `&isAccepted=${filters.isAccepted}`
    }
    if (filters.hadUncompletedCommission !== "undefined" && typeof filters.hadUncompletedCommission !== "undefined") {
      url += `&hadUncompletedCommission=${filters.hadUncompletedCommission}`
    }
    if (filters.isLeakageTest && filters.isLeakageTest !== "undefined") {
      url += `&isLeakageTest=${filters.isLeakageTest}`
    }
    if (filters.isGeneratedPeriodic && filters.isGeneratedPeriodic !== "undefined") {
      url += `&isGeneratedPeriodic=${filters.isGeneratedPeriodic}`
    }
    if (filters.hasDatePlannedVisit && filters.hasDatePlannedVisit !== "undefined") {
      url += `&hasDatePlannedVisit=${filters.hasDatePlannedVisit}`
    }
    if (filters.isThermometerCal && filters.isThermometerCal !== "undefined") {
      url += `&isThermometerCal=${filters.isThermometerCal}`
    }
    if (filters.isService && filters.isService !== "undefined") {
      url += `&isService=${filters.isService}`
    }
    if (filters.isCondenserCleaning && filters.isCondenserCleaning !== "undefined") {
      url += `&isCondenserCleaning=${filters.isCondenserCleaning}`
    }
    if (filters.isCondenserCleaningDry && filters.isCondenserCleaningDry !== "undefined") {
      url += `&isCondenserCleaningDry=${filters.isCondenserCleaningDry}`
    }
    if (filters.isCo2Check && filters.isCo2Check !== "undefined") {
      url += `&isCo2Check=${filters.isCo2Check}`
    }
    if (filters.isSystemCheck && filters.isSystemCheck !== "undefined") {
      url += `&isSystemCheck=${filters.isSystemCheck}`
    }
    if (filters.needsTwo && filters.needsTwo !== "undefined") {
      url += `&needsTwo=${filters.needsTwo}`
    }
    if (filters.internalOrderTypes && filters.internalOrderTypes.length > 3) {
      url += `&internalOrderTypes=${filters.internalOrderTypes}`
    }
    if (filters.isDistanceAssignment && filters.isDistanceAssignment !== "undefined") {
      url += `&isDistanceAssignment=${filters.isDistanceAssignment}`
    }

    if (sortBy.value) {
      const backendSortBy =
        sortBy.value === "DisplayOrder" ? "Priority ASC,DistanceKm ASC,DateCreated DESC" : sortBy.value
      url += `&sortBy=${backendSortBy}`
    }
    if (location.value.lat && location.value.lng) {
      url += `&lat=${location.value.lat}&lng=${location.value.lng}`
    }
    return url
  }

  const getOrdersAsync = async () => {
    startLoading("getOrdersAsync")
    try {
      if (!filters.status.replace(",", "").trim().length) {
        setNumberOfRecords(0)
        orders.value = []
        stopLoading("getOrdersAsync")
        return
      }
      const url = generateWorkOrdersUrl()
      const raw = await fetchAuthAsync(url, "GET")
      if (!Array.isArray(raw.result)) {
        throw raw
      }
      setNumberOfRecords(raw.numberOfRecords)
      if (sortBy.value === "DisplayOrder") {
        orders.value = raw.result.slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      } else {
        orders.value = raw.result
      }
    } catch (e) {
      setError(e)
      orders.value = []
    }
    stopLoading("getOrdersAsync")
  }

  const { paging, setNumberOfRecords, setPageNum, resetPaging } = usePaging(getOrdersAsync)

  const FILTER_KEYS = Object.keys(DEFAULT_FILTERS())

  watch(
    filters,
    () => {
      if (syncQueryParams) {
        const D = DEFAULT_FILTERS()
        const q = {}
        if (filters.status && filters.status !== D.status) q.status = filters.status
        if (filters.customer && filters.customer !== EMPTY_GUID) q.customer = filters.customer
        if (filters.facility && filters.facility !== EMPTY_GUID) q.facility = filters.facility
        if (filters.ownerID) q.ownerID = filters.ownerID
        if (filters.userCreated) q.userCreated = filters.userCreated
        if (filters.searchTerm) q.searchTerm = filters.searchTerm
        if (filters.orderNumber) q.orderNumber = filters.orderNumber
        if (filters.serialNumber) q.serialNumber = filters.serialNumber
        if (filters.projectID) q.projectID = filters.projectID
        if (filters.createdFrom) q.createdFrom = filters.createdFrom
        if (filters.createdTo) q.createdTo = filters.createdTo
        if (filters.completedFrom) q.completedFrom = filters.completedFrom
        if (filters.completedTo) q.completedTo = filters.completedTo
        if (filters.finalizedFrom) q.finalizedFrom = filters.finalizedFrom
        if (filters.finalizedTo) q.finalizedTo = filters.finalizedTo
        if (filters.datePlannedFrom) q.datePlannedFrom = filters.datePlannedFrom
        if (filters.datePlannedTo) q.datePlannedTo = filters.datePlannedTo
        if (filters.completedOrFinalizedYesterday)
          q.completedOrFinalizedYesterday = filters.completedOrFinalizedYesterday
        if (filters.completedOrFinalizedToday) q.completedOrFinalizedToday = filters.completedOrFinalizedToday
        if (filters.isWarranty !== "undefined") q.isWarranty = filters.isWarranty
        if (filters.isSpecial !== "undefined") q.isSpecial = filters.isSpecial
        if (filters.isAttention !== "undefined") q.isAttention = filters.isAttention
        if (filters.isAccepted !== "undefined") q.isAccepted = filters.isAccepted
        if (filters.hadUncompletedCommission !== "undefined")
          q.hadUncompletedCommission = filters.hadUncompletedCommission
        if (filters.isLeakageTest !== "undefined") q.isLeakageTest = filters.isLeakageTest
        if (filters.isService !== "undefined") q.isService = filters.isService
        if (filters.isCondenserCleaning !== "undefined") q.isCondenserCleaning = filters.isCondenserCleaning
        if (filters.isCondenserCleaningDry !== "undefined") q.isCondenserCleaningDry = filters.isCondenserCleaningDry
        if (filters.isCo2Check !== "undefined") q.isCo2Check = filters.isCo2Check
        if (filters.isSystemCheck !== "undefined") q.isSystemCheck = filters.isSystemCheck
        if (filters.needsTwo !== "undefined") q.needsTwo = filters.needsTwo
        if (filters.isThermometerCal !== "undefined") q.isThermometerCal = filters.isThermometerCal
        if (filters.isGeneratedPeriodic !== "undefined") q.isGeneratedPeriodic = filters.isGeneratedPeriodic
        if (filters.hasDatePlannedVisit !== "undefined") q.hasDatePlannedVisit = filters.hasDatePlannedVisit
        if (filters.isDistanceAssignment !== "undefined") q.isDistanceAssignment = filters.isDistanceAssignment
        // Preserve any non-filter query params
        for (const key of Object.keys(route.query)) {
          if (!FILTER_KEYS.includes(key)) {
            q[key] = route.query[key]
          }
        }
        router.replace({ query: q }).catch(() => {})
      }
      resetPaging()
    },
    { deep: true },
  )

  return {
    paging,
    resetPaging,
    orders,
    filters,
    sortBy,
    location,
    resetFilters,
    applyQueryParams,
    getOrdersAsync, // TODO: candidate for removal. we should just watch filters and call getOrdersAsync
    setPageNum,
    DEFAULT_FILTERS,
    downloadAttachmentsAsync,
    selectOrderAsync,
  }
}
